import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import SessionExpiryDialog from '../components/SessionExpiryDialog';
// Importing this installs the axios interceptors that carry the session cookie, the
// portal name and the CSRF token on every request. See ../session.js.
import { clearSession, onSession, revokeSession } from '../session';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const AuthContext = createContext();

// HIPAA: automatic logoff after inactivity (4 hours)
const IDLE_TIMEOUT_MS = 240 * 60 * 1000;

export function useAuth() {
  return useContext(AuthContext);
}

// The server refuses every other call with this 403 while an administrator-set
// password is still in place.
const MUST_CHANGE_PASSWORD_RE = /must change the password/i;

// GET /auth/me is how a reload finds out whether there is a session at all, so its own
// 401 is an answer rather than an error — it must not bounce the browser to /login.
const AUTH_ME_RE = /\/auth\/me$/;

/** The shape the app expects, from the profile the server returns. */
function shapeUser(data) {
  return {
    ...data,
    full_name: data.full_name ?? data.name ?? '',
    practice_name: data.practice_name ?? '',
    org_type: data.org_type || 'clinic',
    org_id: data.org_id ?? null,
    org_name: data.org_name ?? '',
    org_site_count: data.org_site_count ?? 0,
    // A platform admin working inside this clinic. It used to be read out of the JWT
    // (`pae`/`act`); the server now says so directly, including whether they are acting
    // AS this user rather than as themselves.
    platform_entry: !!data.platform_entry,
    acting_as: data.acting_as || null,
  };
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);
  // When the session runs out, in epoch ms. The portal used to read this out of the
  // JWT's `exp`; the server now sends `session_expires_at` with every response that
  // issues a token, and ../session.js hands it here.
  const [expiresAt, setExpiresAt] = useState(null);
  // Kept apart from `user` because `user` is replaced wholesale whenever the server
  // sends a fresh profile, and not every one of those carries this flag.
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const logoutRef = useRef(null);
  const idleTimerRef = useRef(null);
  const sessionExpiredRef = useRef(false);
  const expiresAtRef = useRef(null);

  useEffect(() => {
    sessionExpiredRef.current = sessionExpired;
  }, [sessionExpired]);

  useEffect(() => {
    expiresAtRef.current = expiresAt;
  }, [expiresAt]);

  // Any response that re-issues the session (refresh, password change, rename) moves
  // the expiry, so take it from there rather than only from the calls we make here.
  useEffect(() => onSession(setExpiresAt), []);

  /** Adopt the profile the server just sent as the signed-in user. */
  // Counts every session this page adopts, so a slow answer about an older one
  // can tell it has been overtaken.
  const sessionGenRef = useRef(0);
  const applySession = useCallback((userData) => {
    if (!userData) return;
    sessionGenRef.current += 1;
    setUser(shapeUser(userData));
    setMustChangePassword(!!userData.must_change_password);
    setIsAuthenticated(true);
  }, []);

  // Returns the (never-rejecting) revocation request so a caller that is about to leave
  // the page can wait for it briefly; everyone else can ignore it.
  const logout = useCallback(() => {
    const revoked = revokeSession();
    clearSession();
    setUser(null);
    setIsAuthenticated(false);
    setSessionExpired(false);
    setMustChangePassword(false);
    setExpiresAt(null);
    return revoked;
  }, []);

  useEffect(() => {
    logoutRef.current = logout;
  }, [logout]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        // Mask unexpected/server errors (5xx, network) with a friendly message;
        // keep actionable 4xx messages (validation, auth, conflicts) intact.
        const st = err?.response?.status;
        if (!err?.response || st >= 500) {
          const FRIENDLY = 'Something went wrong. Our team is looking into it.';
          if (!err.response) err.response = { status: 0, data: {} };
          if (!err.response.data || typeof err.response.data !== 'object') err.response.data = {};
          const eid = err.response.data.error_id;
          err.response.data.detail = eid ? `${FRIENDLY} (ref: ${eid})` : FRIENDLY;
          err.userMessage = err.response.data.detail;
        }
        const url = String(err?.config?.url || '');
        const isAuthLogin =
          url.includes('/auth/login') || url.includes('/auth/token') || url.includes('/auth/register');
        const isRefresh = url.includes('/auth/refresh');
        const isMe = AUTH_ME_RE.test(url.split('?')[0]);
        const detail = err?.response?.data?.detail;
        if (st === 403 && typeof detail === 'string' && MUST_CHANGE_PASSWORD_RE.test(detail)) {
          setMustChangePassword(true);
          return Promise.reject(err);
        }
        if (err?.response?.status === 401 && !isAuthLogin && !isRefresh && !isMe) {
          if (sessionExpiredRef.current) {
            return Promise.reject(err);
          }
          // Session expired (or about to) — let the dialog flow handle it instead of
          // a hard logout.
          const expMs = expiresAtRef.current;
          if (expMs && Date.now() >= expMs - 5000) {
            setSessionExpired(true);
            return Promise.reject(err);
          }
          logoutRef.current?.();
          window.location.href = '/login';
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(interceptor);
  }, []);

  // Boot and every reload start here. The session is in a cookie this page cannot read,
  // so the server is the only thing that knows whether there is one: 200 means signed
  // in and carries the profile, the CSRF token and the expiry; 401 means signed out.
  // `loading` stays true until it settles, or a signed-in user is shown the login page
  // for a frame and loses the route they asked for.
  useEffect(() => {
    // The /switch page makes its own session, by redeeming a handoff code — and that
    // redeem REPLACES this portal's cookie. Asking /auth/me here too would send the old
    // cookie in a race with it: when the old answer landed second, the portal showed the
    // role you had just left (its menu, its Administrators item) over a session that was
    // already the new one, and every click on that menu came back 403. So on /switch the
    // redeem is the only thing that decides who you are.
    if (window.location.pathname === '/switch') {
      setLoading(false);
      return undefined;
    }
    const gen = sessionGenRef.current;
    let cancelled = false;
    axios
      .get(`${API_URL}/api/v1/auth/me`)
      .then((res) => {
        // Anything applied since this request left (a switch, a sign-in) is newer than
        // what the cookie said when it was sent; the late answer must not overwrite it.
        if (!cancelled && sessionGenRef.current === gen) applySession(res.data);
      })
      .catch(() => {
        if (!cancelled && sessionGenRef.current === gen) {
          setUser(null);
          setIsAuthenticated(false);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [applySession]);

  // HIPAA: automatic logoff on inactivity
  useEffect(() => {
    if (!isAuthenticated) return;

    const resetIdleTimer = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        // Never log out from under the expiry dialog. It is already counting this
        // session down in front of the user and signs them out itself at zero, so
        // firing here as well ends the session behind it — and "Extend session" then
        // finds nothing to extend and drops them at the login page, which is the one
        // thing the dialog exists to prevent. Automatic logoff still happens, bounded
        // by the countdown the user can actually see; re-arm so the control is not
        // lost if they do extend.
        if (sessionExpiredRef.current) {
          resetIdleTimer();
          return;
        }
        logoutRef.current?.();
      }, IDLE_TIMEOUT_MS);
    };

    resetIdleTimer();
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach((e) => window.addEventListener(e, resetIdleTimer));
    return () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      events.forEach((e) => window.removeEventListener(e, resetIdleTimer));
    };
  }, [isAuthenticated]);

  // Trigger expiry dialog when the session runs out — wait for tab visibility if the
  // user is elsewhere.
  useEffect(() => {
    if (!isAuthenticated || !expiresAt) return;

    let visibilityHandler = null;
    const cleanupVisibility = () => {
      if (visibilityHandler) {
        document.removeEventListener('visibilitychange', visibilityHandler);
        visibilityHandler = null;
      }
    };

    const triggerDialog = () => {
      if (document.visibilityState === 'visible') {
        setSessionExpired(true);
      } else {
        visibilityHandler = () => {
          if (document.visibilityState === 'visible') {
            cleanupVisibility();
            setSessionExpired(true);
          }
        };
        document.addEventListener('visibilitychange', visibilityHandler);
      }
    };

    const delay = Math.max(0, expiresAt - Date.now());
    const timeoutId = setTimeout(triggerDialog, delay);
    return () => {
      clearTimeout(timeoutId);
      cleanupVisibility();
    };
  }, [isAuthenticated, expiresAt]);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/v1/auth/login/clinic`, {
      email,
      password
    });
    const { user: userData } = response.data;
    applySession(userData);
    return userData;
  };

  // Leaving a platform-entry session: end it here and go back to the admin portal. The
  // platform admin's own session is a separate cookie for a separate portal and is
  // untouched, so they land back where they started, still signed in.
  const exitPlatformEntry = useCallback(async (adminPortalUrl) => {
    // Give the revocation a moment to leave before the full-page navigation cancels it.
    await logout();
    window.location.href = adminPortalUrl;
  }, [logout]);

  /**
   * After POST /auth/me/password succeeds. The change signs out every earlier token
   * (including the one that made the request) and issues a new one in the cookie, so
   * adopt the profile and CSRF token that came back with it.
   */
  const completePasswordChange = useCallback((data) => {
    if (data?.user) {
      applySession(data.user);
    }
    setMustChangePassword(!!data?.user?.must_change_password);
  }, [applySession]);

  // Unified sign-in: authenticate once, then land in the identity's home portal. If
  // that's THIS (clinic) portal, sign in here; otherwise hand off to the right portal
  // (e.g. a patient who typed their credentials on the provider login). Returns
  // { routed } — routed=true means the browser is redirecting elsewhere.
  const unifiedLogin = useCallback(async (email, password) => {
    const res = await axios.post(`${API_URL}/api/v1/auth/login`, { email, password });
    const { user: userData, default: home, access_token: handoffAuth } = res.data;
    // Only hand off to the other consumer portal (patient). super_admin ('admin') is
    // isolated and cannot hand off, so it signs in locally here (the clinic portal
    // shows its platform dashboard).
    if (home && home.portal === 'patient') {
      // The one place this portal touches the token. The identity's home is the
      // patient portal, so the server deliberately set no cookie here — it will not
      // leave a patient session sitting in the provider portal's cookie. That makes
      // this call the exception: it authenticates with the token from the response,
      // which is used once, never stored, and dropped when this function returns.
      const handoff = await axios.post(
        `${API_URL}/api/v1/auth/switch-portal`,
        { role: home.role, clinic_id: home.clinic_id || null },
        { headers: { Authorization: `Bearer ${handoffAuth}` } },
      );
      window.location.assign(handoff.data.handoff_url);
      return { routed: true };
    }
    applySession(userData);
    return { routed: false };
  }, [applySession]);

  const extendSession = useCallback(async () => {
    try {
      const response = await axios.post(`${API_URL}/api/v1/auth/refresh`, {});
      applySession(response.data.user);
      setSessionExpired(false);
    } catch (err) {
      // Nothing left to extend. Closing the dialog on a dead session looked like the
      // click had worked; go where the user actually is instead.
      setSessionExpired(false);
      logout();
      window.location.href = '/login';
    }
  }, [applySession, logout]);

  const handleExpiryLogout = useCallback(() => {
    logout();
    window.location.href = '/login';
  }, [logout]);

  const clinicId = user?.clinic_id || null;

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    unifiedLogin,
    logout,
    applySession,
    clinicId,
    isPlatformEntry: !!user?.platform_entry,
    exitPlatformEntry,
    mustChangePassword,
    completePasswordChange,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <SessionExpiryDialog
        open={sessionExpired}
        onExtend={extendSession}
        onLogout={handleExpiryLogout}
      />
    </AuthContext.Provider>
  );
}
